// Cross-app navigation URLs (landing site <-> dashboard app).
//
// KX-2 split: the marketing/landing site (konectx.net) and the dashboard app
// (app.konectx.net) may be deployed separately. Navigation between them must be
// environment-configured — NO hardcoded domains in code.
//
//   VITE_LANDING_URL   – absolute URL of the landing site (e.g. https://konectx.net)
//   VITE_DASHBOARD_URL – absolute URL of the dashboard app (e.g. https://app.konectx.net)
//
// Interim (single deployment, before KX-2): leave these unset and navigation
// stays within the SPA, so nothing breaks today. Set the production values with
// KX-2.

import type { NavigateFunction } from 'react-router-dom';

/**
 * An in-app navigator. Accepts react-router's NavigateFunction (so callers can
 * pass `navigate` from useNavigate directly) or any `(path) => void` function.
 */
export type SpaNavigate = NavigateFunction | ((path: string) => void);

const env = (import.meta as unknown as { env?: Record<string, string> }).env ?? {};

/** Absolute URL of the landing site, or undefined when same-app (interim). */
export const LANDING_URL: string | undefined = env.VITE_LANDING_URL?.trim() || undefined;

/** Absolute URL of the dashboard app, or undefined when same-app (interim). */
export const DASHBOARD_URL: string | undefined = env.VITE_DASHBOARD_URL?.trim() || undefined;

/** True if the given absolute URL points at a different origin than the current page. */
function isCrossOrigin(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Navigate from the dashboard back to the landing site.
 *
 * - If VITE_LANDING_URL is configured and cross-origin, do a full-page navigation
 *   to it (this is the KX-2 case, and fixes the broken back button that used to
 *   stay inside the dashboard SPA).
 * - Otherwise fall back to the provided in-app navigator (interim, same deployment).
 */
export function goToLandingSite(spaNavigate?: SpaNavigate): void {
  if (LANDING_URL && isCrossOrigin(LANDING_URL)) {
    window.location.href = LANDING_URL;
    return;
  }
  if (LANDING_URL) {
    // Configured but same-origin: still a full navigation to the configured path.
    window.location.href = LANDING_URL;
    return;
  }
  // Interim: no landing URL configured — stay in the SPA at the landing route.
  if (spaNavigate) spaNavigate('/');
  else window.location.href = '/';
}

/**
 * Navigate from the landing site to the dashboard. Provided for completeness and
 * the landing-side "Log in / Dashboard" link (handled on the landing side).
 */
export function goToDashboard(spaNavigate?: SpaNavigate): void {
  if (DASHBOARD_URL) {
    window.location.href = DASHBOARD_URL;
    return;
  }
  if (spaNavigate) spaNavigate('/signin');
  else window.location.href = '/signin';
}
